<script setup lang="ts">
import { GraduationCap } from 'lucide-vue-next'
import SubjectsTaughtTable from '@/Components/Tutors/SubjectsTaughtTable.vue'

/**
 * What the tutor teaches, on its own tab and open.
 *
 * The table lived inside Overview behind a "see levels and boards" toggle, which
 * asked the one question a parent comes to a tutor profile with — do you teach
 * my child's subject at my child's level — and then hid the answer behind a
 * click. It is the tab now, and it is expanded: there is nothing here that
 * benefits from being folded away.
 */
interface Subject {
  id: number
  name: string
}

interface Examboard {
  id: number
  name: string
}

interface Teaching {
  subject_id: number
  subject: string
  levels: string[]
  examboards: string[]
  rows?: Array<{ level: string; examboards: string[] }>
}

defineProps<{
  teaching?: Teaching[]
  subjects?: Subject[]
  examboards?: Examboard[]
}>()
</script>

<template>
  <div class="space-y-6">
    <div v-if="teaching?.length">
      <h3 class="mb-4 text-xl font-semibold text-ink">Subjects tutored</h3>
      <SubjectsTaughtTable :teaching="teaching" />
    </div>

    <!--
      A tutor recorded before the per-subject breakdown existed has subjects and
      boards as two flat lists and no way to pair them. They are shown as they
      are rather than joined into a table that would invent the pairing.
    -->
    <template v-else>
      <div v-if="subjects?.length">
        <h3 class="mb-3 text-xl font-semibold text-ink">Subjects tutored</h3>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="subject in subjects"
            :key="subject.id"
            class="rounded-md border border-line-brand bg-brand-soft px-4 py-2 text-sm font-semibold text-brand-soft-text"
          >
            {{ subject.name }}
          </span>
        </div>
      </div>

      <div v-if="examboards?.length">
        <h3 class="mb-3 text-xl font-semibold text-ink">Exam boards covered</h3>
        <ul class="flex flex-wrap gap-2">
          <li
            v-for="examboard in examboards"
            :key="examboard.id"
            class="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-sm font-semibold text-ink"
          >
            <GraduationCap class="size-4 shrink-0 text-brand" />
            {{ examboard.name }}
          </li>
        </ul>
      </div>

      <p v-if="!subjects?.length && !examboards?.length" class="text-sm text-muted">
        This tutor has not recorded the subjects they teach yet.
      </p>
    </template>
  </div>
</template>
